import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// ── Styles ────────────────────────────────────────────────────

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modal = {
  background: 'var(--card-bg)', borderRadius: '12px',
  maxWidth: '520px', width: '92%', maxHeight: '90vh',
  overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  display: 'flex', flexDirection: 'column'
};

const headerStyle = {
  padding: '20px 24px 0', textAlign: 'center'
};

const bodyStyle = {
  padding: '16px 24px', flex: 1, overflowY: 'auto'
};

const footerStyle = {
  padding: '16px 24px 20px',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  borderTop: '1px solid var(--border)'
};

const dotBase = {
  width: 10, height: 10, borderRadius: '50%',
  border: '2px solid var(--primary)', cursor: 'pointer',
  transition: 'background 0.2s'
};

const thresholdItem = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '8px 12px', borderRadius: '6px',
  backgroundColor: 'var(--bg)', marginBottom: '6px',
  fontSize: '12px'
};

const productItem = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '8px 12px', borderRadius: '6px',
  border: '1px solid var(--border)', marginBottom: '6px',
  fontSize: '12px'
};

const challengeItem = {
  padding: '10px 12px', borderRadius: '6px',
  border: '1px solid var(--border)', marginBottom: '6px',
  fontSize: '12px'
};

const noteBox = {
  padding: '10px 12px', borderRadius: '6px',
  backgroundColor: 'var(--bg)', fontSize: '11px',
  color: 'var(--text-muted)', marginTop: '12px',
  lineHeight: '1.5'
};

const ruleItem = {
  padding: '10px 0', fontSize: '12px', lineHeight: '1.6',
  borderBottom: '1px solid var(--border)'
};

const fmt = (v) => Number(v || 0).toFixed(2);

const fmtRange = (min, max) => {
  if (!max) return `od ${fmt(min)} zl`;
  return `${fmt(min)} \u2013 ${fmt(max)} zl`;
};

// ════════════════════════════════════════════════════════════════

export default function BonusTutorial({ isOpen, onClose, locationId }) {
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const totalSteps = 6;

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const url = locationId ? `/bonus/config?location_id=${locationId}` : '/bonus/config';
      const res = await api.get(url);
      if (res.data.success) setConfig(res.data.data);
    } catch (err) {
      console.error('Blad wczytywania konfiguracji premii:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchConfig();
      setStep(0);
    }
  }, [isOpen, fetchConfig]);

  if (!isOpen) return null;

  const thresholds = config?.progi || [];
  const products = config?.produkty || [];
  const challenges = config?.wyzwania || [];
  const firstThreshold = thresholds.length > 0 ? thresholds[0] : null;

  const prev = () => setStep((s) => Math.max(0, s - 1));
  const next = () => setStep((s) => Math.min(totalSteps - 1, s + 1));

  // ── Step 0: Powitanie ──────────────────────────────────────

  const renderStep0 = () => (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '12px' }}>&#127942;</div>
      <h3 style={{ margin: '0 0 12px', fontSize: '18px', color: 'var(--text)' }}>
        System premii
      </h3>
      <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.7' }}>
        Za swoja sprzedaz mozesz zarobic dodatkowa premie.
        <br />Premia sklada sie z <strong>trzech czesci</strong>:
      </p>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
        {[
          { icon: '\u{1F4CA}', label: 'Premia bazowa', desc: 'za obrot', color: 'var(--success)' },
          { icon: '\u2B50', label: 'Premia produktowa', desc: 'za sztuki', color: 'var(--purple)' },
          { icon: '\u{1F3AF}', label: 'Wyzwania', desc: 'cele specjalne', color: 'var(--warning)' },
        ].map((item, i) => (
          <div key={i} style={{
            padding: '10px', borderRadius: '8px', border: `2px solid ${item.color}`,
            backgroundColor: 'var(--bg)', textAlign: 'center', flex: '1', minWidth: '100px'
          }}>
            <div style={{ fontSize: '24px' }}>{item.icon}</div>
            <div style={{ fontSize: '11px', fontWeight: '600', marginTop: '4px' }}>{item.label}</div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{item.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Step 1: Premia bazowa ──────────────────────────────────

  const renderStep1 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>&#128202;</div>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text)' }}>
          Premia bazowa &mdash; za obrot
        </h3>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Im wiecej sprzedasz w miesiacu, tym wyzsza premia.
          <br />Liczy sie <strong>cala Twoja sprzedaz</strong> &mdash; ze wszystkich lokalizacji.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Wczytywanie progow...
        </div>
      ) : thresholds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak skonfigurowanych progow.
        </div>
      ) : (
        <div>
          {thresholds.map((t, i) => {
            const nextT = thresholds[i + 1];
            const maxVal = nextT ? Number(nextT.min_sprzedaz || 0) : null;
            const minVal = Number(t.min_sprzedaz || 0);
            const premia = Number(t.premia_bazowa || 0);
            return (
              <div key={i} style={thresholdItem}>
                <span>Sprzedaz {fmtRange(minVal, maxVal)}</span>
                <span style={{ fontWeight: 600, color: 'var(--success)' }}>{fmt(premia)} zl premii</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={noteBox}>
        <strong>Jak dzialaja progi proporcjonalne?</strong>
        <br />Progi sa przeliczane proporcjonalnie do Twoich dni pracy.
        {firstThreshold && (
          <span>
            <br />Przyklad: prog {fmt(firstThreshold.min_sprzedaz)} zl przy 22 dniach roboczych.
            Jesli przepracujesz 15 dni, Twoj prog wynosi{' '}
            <strong>{fmt(Number(firstThreshold.min_sprzedaz) * 15 / 22)} zl</strong>.
          </span>
        )}
      </div>
    </div>
  );

  // ── Step 2: Premia produktowa ──────────────────────────────

  const renderStep2 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>&#11088;</div>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text)' }}>
          Produkty premiowane
        </h3>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Za sprzedaz wybranych produktow dostajesz dodatkowa premie za kazda sztuke.
          <br />Sprzedaz ze <strong>wszystkich lokalizacji</strong> sumuje sie razem.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Wczytywanie produktow...
        </div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '12px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak skonfigurowanych produktow premiowych.
        </div>
      ) : (
        <div>
          {products.map((p, i) => (
            <div key={i} style={productItem}>
              <div>
                <span style={{ marginRight: '6px' }}>&#11088;</span>
                <span style={{ fontWeight: 500 }}>{p.nazwa || p.produkt_nazwa || 'Produkt'}</span>
                {(p.min_ilosc > 0 || p.min_marza_procent > 0) && (
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', paddingLeft: '22px' }}>
                    {p.min_ilosc > 0 && <span>Min. {p.min_ilosc} szt./mies. </span>}
                    {p.min_marza_procent > 0 && <span>| Min. marza {p.min_marza_procent}%</span>}
                  </div>
                )}
              </div>
              <span style={{ fontWeight: 600, color: 'var(--purple)', whiteSpace: 'nowrap' }}>
                +{fmt(p.kwota_za_sztuke || p.kwota_za_szt || 0)} zl/szt.
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={noteBox}>
        <strong>Jak rozpoznac produkty premiowe?</strong>
        <br />Na kasie (POS) sa oznaczone gwiazdka &#11088; obok nazwy.
        <br />Jesli udzielisz rabatu i marza spadnie ponizej minimum &mdash; ta sztuka nie liczy sie do premii.
      </div>
    </div>
  );

  // ── Step 3: Wyzwania ──────────────────────────────────────

  const renderStep3 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>&#127919;</div>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text)' }}>Wyzwania</h3>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Specjalne cele z dodatkowymi nagrodami.
          <br />Realizuj je w dowolnej lokalizacji &mdash; postep sumuje sie globalnie.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Wczytywanie wyzwan...
        </div>
      ) : challenges.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: '12px' }}>
          Aktualnie brak aktywnych wyzwan.
          <br />Nowe wyzwania pojawiaja sie na poczatku miesiaca.
        </div>
      ) : (
        <div>
          {challenges.map((ch, i) => (
            <div key={i} style={challengeItem}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600 }}>{ch.nazwa}</span>
                <span style={{ fontWeight: 600, color: 'var(--success)' }}>{fmt(ch.nagroda)} zl</span>
              </div>
              {ch.opis && (
                <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{ch.opis}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={noteBox}>
        Postep wyzwan widzisz w widgecie premii na kasie.
      </div>
    </div>
  );

  // ── Step 4: Jak sie liczy ──────────────────────────────────

  const renderStep4 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>&#128736;&#65039;</div>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text)' }}>
          Jak sie liczy premia?
        </h3>
      </div>

      <div style={{ fontSize: '12px', lineHeight: '1.7' }}>
        <div style={{ ...ruleItem, display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#128176;</span>
          <div>
            <strong>Utarg ze wszystkich lokalizacji</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Jesli pracujesz w kilku sklepach, Twoja sprzedaz sumuje sie razem.
              Zastepstwa tez sie licza!
            </span>
          </div>
        </div>

        <div style={{ ...ruleItem, display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#128197;</span>
          <div>
            <strong>Progi proporcjonalne do dni pracy</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              System liczy Twoje unikalne dni pracy (ze zmian). Im wiecej dni przepracujesz,
              tym wyzszy prog &mdash; ale tez wyzszy utarg.
            </span>
          </div>
        </div>

        <div style={{ ...ruleItem, display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#11088;</span>
          <div>
            <strong>Produkty premiowane</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Sztuki sumowane ze wszystkich lokalizacji. Liczy sie tylko sprzedaz z odpowiednia marza.
            </span>
          </div>
        </div>

        <div style={{ ...ruleItem, borderBottom: 'none', display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#9989;</span>
          <div>
            <strong>Zatwierdzenie</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Na koniec miesiaca kierownik oblicza i zatwierdza premie.
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Step 5: Gdzie to widze? ────────────────────────────────

  const renderStep5 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '36px', marginBottom: '8px' }}>&#128064;</div>
        <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text)' }}>
          Gdzie sledze swoja premie?
        </h3>
      </div>

      <div style={{ fontSize: '12px', lineHeight: '1.7' }}>
        <div style={{ ...ruleItem, display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#128187;</span>
          <div>
            <strong>Na kasie (POS)</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Widget premii pokazuje Twoj biezacy postep w czasie rzeczywistym &mdash;
              ile sprzedales, jaka premia, ile brakuje do nastepnego progu.
            </span>
          </div>
        </div>

        <div style={{ ...ruleItem, display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#128200;</span>
          <div>
            <strong>Moja premia (ta strona)</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Szczegolowa tabela sprzedazy produktow premiowych z paragonami,
              marza i statusem kazdej pozycji.
            </span>
          </div>
        </div>

        <div style={{ ...ruleItem, borderBottom: 'none', display: 'flex', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>&#11088;</span>
          <div>
            <strong>Produkty premiowe na kasie</strong>
            <br />
            <span style={{ color: 'var(--text-muted)' }}>
              Oznaczone gwiazdka &#11088; na liscie produktow.
              Widzisz od razu ile zarobisz za kazda sztuke.
            </span>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>&#128170;</div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--primary)' }}>
          Powodzenia! Im wiecej sprzedasz, tym wiecej zarobisz.
        </div>
      </div>
    </div>
  );

  const steps = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Krok {step + 1} z {totalSteps}
          </div>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {steps[step]()}
        </div>

        {/* Footer with navigation */}
        <div style={footerStyle}>
          <button
            className="btn"
            style={{ fontSize: '12px', visibility: step === 0 ? 'hidden' : 'visible' }}
            onClick={prev}
          >
            &larr; Wstecz
          </button>

          {/* Step dots */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                style={{
                  ...dotBase,
                  backgroundColor: i === step ? 'var(--primary)' : 'transparent'
                }}
                onClick={() => setStep(i)}
                title={`Krok ${i + 1}`}
              />
            ))}
          </div>

          {step < totalSteps - 1 ? (
            <button className="btn btn-primary" style={{ fontSize: '12px' }} onClick={next}>
              Dalej &rarr;
            </button>
          ) : (
            <button className="btn btn-primary" style={{ fontSize: '12px' }} onClick={onClose}>
              Zamknij
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
